import React, { useEffect, useRef, useState } from "react";
import Button from "../../../form/Button";
import { lngDetect } from "../../../../helpers/lngDetect";
import Form from "../../../form/Form";
import useForm from "../../../../hooks/useForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Input from "../../../form/Input";
import { orderItemEdit } from "../../../../services/orderService";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import TicketPDF from "./DownloadTicketPdf";
import PDFDownloadButton from "./PdfDownloadButton";
import dayjs from "dayjs";
import useClickOutside from "../../../../hooks/useClickOutside";
import Badge from "../../../elements/Badge";
import Image from "../../../elements/Image";
import bookedImg from "../../../../assets/images/booked.svg";
import checkedImg from "../../../../assets/images/checked.svg";
import QRCodeStyling from "qr-code-styling";
import icon from "../../../../assets/images/no-data-found.png";
import { t } from "i18next";
import { useSelector } from "react-redux";

const option = (data) => ({
  type: "canvas",
  width: 150,
  height: 150,
  data,
  margin: 0,
  qrOptions: {
    typeNumber: "0",
    mode: "Byte",
    errorCorrectionLevel: "Q",
  },
  imageOptions: {
    saveAsBlob: true,
    hideBackgroundDots: true,
    imageSize: 0.25, // Reduce logo size relative to canvas
    margin: 0,
  },
  dotsOptions: {
    // type: "extra-rounded",
    type: "dots",
    color: "#6a1a4c",
    roundSize: true,
  },
  backgroundOptions: { round: 0, color: "#ffffff" },
  image: icon,
  cornersSquareOptions: { type: "extra-rounded", color: "#000000" },
  cornersDotOptions: { type: "", color: "#000000" },
});

const Ticket = ({ orderData = {}, eventData = {}, ticket = {} }) => {
  const divRef = useRef();
  const { orderId } = useParams();
  const queryClient = useQueryClient();
  const [isEdit, setIsEdit] = useState(false);
  const { values, handleChange, handleSubmit, errors, setFieldsValue } =
    useForm();
  const { appliedTheme, selectedElement, status } = useSelector(
    (state) => state?.themeProvider
  );

  useClickOutside(divRef, () => {
    setIsEdit(false);
  });

  const eventName = eventData?.title?.en ?? "Untitled Event";
  const venue = [
    eventData?.address?.name,
    eventData?.address?.city,
    eventData?.address?.state,
    eventData?.address?.country,
  ]
    .filter(Boolean)
    .join(", ");

  const [qrCodeImg, setQrCodeImg] = useState(null);
  const date = dayjs(eventData?.startDate).format("dddd, MMMM D, YYYY");
  const startTime = eventData?.startDate;
  const endTime = eventData?.endDate;
  const ticketName = lngDetect(ticket?.ticketInfo?.name);
  const paymentType = ticket?.ticketInfo?.paymentType;
  const completePayment = ticket?.fullPaid;

  const attendee_info = {
    ...Object.assign(
      ...ticket?.attendeeData?.map((item) => ({
        [item?.question]: item?.answers,
      })),
      {}
    ),
  };

  const orderID = orderData?.uid ?? "UNKNOWN";
  const orderedAt = dayjs().format("MMMM D, YYYY h:mm A");
  const eventImageUrl = eventData?.gallery?.[0]?.url || "";

  useEffect(() => {
    if (!ticket?.ticketInfo) return;

    const generateQR = async () => {
      try {
        const qr_data = {
          name: `${attendee_info?.["First Name"]} ${attendee_info?.["Last Name"]} (${attendee_info?.["Email"]})`,
          "order id": orderData?.orderId,
          "event id": eventData?.uid,
          "ticket id": ticket?.ticketInfo?.uid,
          "ticket uid": ticket?.uid,
          ticket: lngDetect(ticket?.ticketInfo?.name),
        };

        const ticketInfo = Object.keys(qr_data)
          .map((key) => `${key}: ${qr_data[key]}`)
          .join("\n");

        const qrCode = new QRCodeStyling(option(ticketInfo));

        // Export QR as blob and convert to object URL
        const blob = await qrCode.getRawData("png");
        const url = URL.createObjectURL(blob);
        setQrCodeImg(url);
      } catch (err) {
        console.error("QR generation failed:", err);
      }
    };

    generateQR();
  }, [ticket]);

  const ticketData = {
    eventName: eventName.toUpperCase(),
    venue,
    date,
    startTime,
    endTime,
    orderId: orderID,
    // userName: `${(attendee_info?.["First Name"] ?? "").slice(0, 3)}**** ${(
    //   attendee_info?.["Last Name"] ?? ""
    // ).slice(0, 3)}*****`,
    userName: `${attendee_info?.["First Name"] || ""} ${
      attendee_info?.["Last Name"] || ""
    }`,
    orderedAt,
    ticketName,
    paymentType,
    completePayment,
    eventImageUrl,
    qrUrl: qrCodeImg, // or base64 string
  };

  useEffect(() => {
    if (orderData?._id) {
      setFieldsValue({
        // firstName: ticket?.attendeeInfo?.firstName,
        // lastName: ticket?.attendeeInfo?.lastName,
        // email: ticket?.attendeeInfo?.email,
        ...Object.assign(
          ...ticket?.attendeeData
            ?.filter((item) => item?.isEdited)
            ?.map((item) => ({ [item?.question]: item?.answers })),
          {}
        ),
      });
    }
  }, [orderData?._id]);

  const { mutate: saveAttendeeHandler, isPending: saveAttendeeInPending } =
    useMutation({
      mutationFn: async (value) => {
        const attendee_info_data = { ...attendee_info, ...value };
        const payload = {
          attendeeData: Object.keys(attendee_info_data)?.map((key) => ({
            question: key,
            answers: attendee_info_data?.[key],
            ...(["First Name", "Last Name", "Email"].includes(key)
              ? {
                  isEdited: true,
                }
              : { isEdited: false }),
          })),
        };
        const res = await orderItemEdit({
          orderItemId: ticket?._id,
          ...payload,
        });
        return res;
      },
      onSuccess: (res) => {
        if (res?.status === 200) {
          queryClient.setQueryData(
            ["order-ticket-item", orderData?.uid],
            (state) => {
              let state_data = JSON.parse(JSON.stringify(state || {}));
              return {
                ...state_data,
                data: {
                  ...state_data?.data,
                  orderItemData: state_data?.data?.orderItemData?.map((item) =>
                    item?._id === ticket?._id
                      ? {
                          ...item,
                          attendeeData: res?.data?.attendeeData,
                        }
                      : item
                  ),
                },
              };
            }
          );
          setIsEdit(false);
        } else {
          toast.warning(res?.message || res?.error);
        }
      },
    });

  function isPaymentComplete({ isFullPaid, status }) {
    if (status === "confirmed" && isFullPaid) {
      return true;
    }
    if (status === "processing" && isFullPaid) {
      return false;
    }
    if (status === "processing" && !isFullPaid) {
      return false;
    }
  }

  return (
    <Form handleSubmit={handleSubmit} onSubmit={saveAttendeeHandler}>
      <div
        ref={divRef}
        className="bg-gradient-to-br from-sky-50 via-white to-slate-100 p-6 rounded-3xl border border-slate-200 shadow-md relative"
      >
        {/* {ticket?.checkIn?.value && (
          <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center opacity-20">
            <Image src={checkedImg} alt={"checked"} classes={"w-2/3 h-2/3"} />
          </div>
        )} */}
        <div className="-mt-10 mb-3 flex items-center gap-5 w-fit">
          <Badge
            type={"success"}
            label={t(
              ticket?.ticketInfo?.type === "addons-event" ? "addon" : "Ticket"
            )}
          />
        </div>
        <div className="flex flex-wrap gap-2 md:items-center justify-between mb-2">
          <div className="flex items-center gap-2 w-1/2">
            <i className="fa-solid fa-ticket text-primary" />
            <h3 className="text-xl font-bold text-slate-800 truncate w-72">
              {lngDetect(ticket?.ticketInfo?.name)}
            </h3>
          </div>

          <div className="flex gap-3">
            {orderData?.status?.info !== "canceled" &&
              isPaymentComplete({
                isFullPaid: orderData?.fullPaid === "yes",
                status: orderData?.status?.info,
              }) && (
                <div className="flex flex-wrap gap-4 flex-shrink-0">
                  <PDFDownloadButton
                    document={
                      <TicketPDF
                        data={ticketData}
                        siteSettings={appliedTheme}
                      />
                    }
                    fileName={`ticket-${ticketData.orderId}.pdf`}
                    className="text-white rounded-full px-3 py-2 md:py-3 text-xs h-auto"
                    label="Download Ticket"
                  />
                </div>
              )}
            {orderData?.status?.info !== "canceled" && (
              <>
                {!isEdit ? (
                  <Button
                    buttonLabel={t("Edit")}
                    buttonLabelClasses="text-lightPurple text-sm"
                    buttonIcon="fa-regular fa-pen-to-square"
                    buttonClasses="bg-transparent px-2 h-auto text-lightPurple"
                    buttonType="button"
                    buttonFunction={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsEdit(true);
                    }}
                  />
                ) : (
                  <Button
                    buttonType="submit"
                    buttonLabel={t("Save")}
                    buttonLabelClasses="text-lightPurple text-sm"
                    buttonIcon="fa-regular fa-save"
                    buttonClasses="bg-transparent px-2 h-auto text-lightPurple"
                    {...(saveAttendeeInPending && {
                      loading: true,
                      buttonIconPosition: "left",
                      isDisable: true,
                    })}
                  />
                )}
              </>
            )}
          </div>
        </div>
        <div className="text-sm text-slate-700 space-y-4">
          <div>
            <div className="mb-4 pb-2 font-semibold text-slate-600 border-b">
              {t("Contact Information")}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-slate-500">{t("First Name")}</div>
                <div className="font-medium text-slate-800">
                  {!isEdit ? (
                    attendee_info?.["First Name"]
                  ) : (
                    <Input
                      value={values?.["First Name"]}
                      inputPlaceholder={t("Enter first name")}
                      onChange={handleChange}
                      inputName={"First Name"}
                    />
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">{t("Last Name")}</div>
                <div className="font-medium text-slate-800">
                  {!isEdit ? (
                    attendee_info?.["Last Name"]
                  ) : (
                    <Input
                      value={values?.["Last Name"]}
                      inputPlaceholder={t("Enter last name")}
                      onChange={handleChange}
                      inputName={"Last Name"}
                    />
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">{t("Email")}</div>
                <div className="font-medium text-slate-800">
                  {!isEdit ? (
                    attendee_info?.Email
                  ) : (
                    <Input
                      inputType="email"
                      inputPlaceholder={t("Enter email")}
                      value={values?.Email}
                      onChange={handleChange}
                      inputName={"Email"}
                    />
                  )}
                </div>
              </div>
              {(ticket?.checkIn?.value ||
                ticket?.ticketInfo?.fullPaid === "no") && (
                <div className="flex items-center gap-1">
                  <Badge
                    type={
                      ticket?.checkIn?.value
                        ? "success"
                        : orderData?.status?.info === "canceled"
                        ? "danger"
                        : "warning"
                    }
                    className={"rounded-md"}
                    label={
                      ticket?.checkIn?.value
                        ? "Check-in"
                        : orderData?.status?.info !== "canceled"
                        ? "booked"
                        : "cancled"
                    }
                  />
                  {ticket?.checkIn?.value
                    ? dayjs(ticket?.checkIn?.date)?.format(
                        "DD MMM, YYYY hh:mm A"
                      )
                    : orderData?.status?.info !== "canceled" && (
                        <div className="h-7 w-7">
                          <Image src={bookedImg} alt={"booked"} />
                        </div>
                      )}
                </div>
              )}
            </div>
          </div>
          <div>
            <div className="mb-4 pb-2 font-semibold text-slate-600 border-b">
              {t("Ticket Information")}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-slate-500">{t("Total Price")}</div>
                <div className="font-medium text-slate-800">
                  {eventData?.currency?.symbol || "$"}{" "}
                  {parseFloat(ticket?.ticketInfo?.fullPrice).toFixed(2)}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">
                  {t("Deposite Price")}
                </div>
                <div className="font-medium text-slate-800">
                  {eventData?.currency?.symbol || "$"}{" "}
                  {parseFloat(ticket?.ticketInfo?.price).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* <div className="fixed bg-white top-0 left-0 w-screen h-screen !m-0">
        <TicketPDF data={ticketData} siteSettings={appliedTheme} />
      </div> */}
    </Form>
  );
};

export default Ticket;
